import { useEffect, useRef, useState } from 'react';
import { Field, Formik } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { useCookies } from 'react-cookie';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FormFeedback } from 'reactstrap';
import images from '../../assets/images';
import ReactstrapFormikFloatingLabelPasswordInput from '../../components/reactstrap-formik/ReactstrapFormikFloatingLabelPasswordInput';
import RecaptchaV2Net from '../../components/RecaptchaV2Net';
import type { ILoginForm } from '../../models/login';
import { getAPIKey } from '../../services/login/login-service';
import { store } from '../../store';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import { LoginPasswordValidationSchema } from '../../validation/login/login-validation';

const SignInWithPassword = ({ email, handleSubmit, onChangeStage, setPassword }) => {
  const [siteKey, setSiteKey] = useState<string>('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isRecaptchaV2, setIsRecaptchaV2] = useState(false);
  const [cookies, setCookie] = useCookies(['driver_remember', 'driver_email', 'driver_password']);

  useEffect(() => {
    getRecaptchaSiteKey();
  }, []);

  const getRecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    });
    const siteKey = config.data.find(
      (item) => item.reference_code === Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    )?.value;
    setSiteKey(siteKey ?? '');
  };

  const rememberValue = cookies.driver_remember === 'true';

  const initialState = {
    email,
    password: rememberValue ? cookies.driver_password : '',
    rememberMe: rememberValue,
    recaptchaCode: '',
  };

  const handleLoginSubmit = async (value: ILoginForm, actions) => {
    actions.setFieldValue('recaptchaCode', '');
    actions.setFieldTouched('recaptchaCode', false);

    setPassword(value.password);

    const date = new Date();
    date.setDate(date.getDate() + 30);

    setCookie('driver_remember', value.rememberMe, {
      path: '/',
      expires: date,
    });

    if (value.rememberMe) {
      setCookie('driver_email', value.email.toLowerCase(), {
        path: '/',
        expires: date,
      });
      setCookie('driver_password', value.password, {
        path: '/',
        expires: date,
      });
    }

    try {
      let token = '';
      if (!isRecaptchaV2 && executeRecaptcha) {
        token = await executeRecaptcha('login');
        if (!token) {
          setIsRecaptchaV2(true);
          return;
        }
        value.recaptchaCode = token;
        handleSubmit(value, isRecaptchaV2, setIsRecaptchaV2);
        return;
      }
      handleSubmit(value, isRecaptchaV2, setIsRecaptchaV2);
    } catch {
      setIsRecaptchaV2(true);
    } finally {
      if (isRecaptchaV2 && recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const { t } = useTranslation();
  return (
    <div className="password-form-container font-poppins">
      <Helmet>
        <title>{companyNameReplace(t('login.title'))}</title>
      </Helmet>
      <button
        type="button"
        className="btn btn-icon btn-link pr-0 btn-back"
        onClick={() => onChangeStage(Common.AuthStage.SIGNIN)}
        title={t<string>('signup.button.back')}
      >
        <img className="icon" src={images.backArrowRSVG} alt={t<string>('signup.button.back')} />
      </button>
      <div className="sign-up-header">
        <div className="sign-up-icon-container">
          <img src={images.loginIconSVG} alt={companyNameReplace(t<string>('login.label.logo'))} />
        </div>
        <h1 className="title sign-in-title mt-3" style={{ textTransform: 'capitalize' }}>
          {t('login.header')}
        </h1>
        <p className="sign-up-sub-header">{t('login.subHeader')}</p>
      </div>

      <Formik
        initialValues={initialState}
        onSubmit={handleLoginSubmit}
        validationSchema={LoginPasswordValidationSchema(t, isRecaptchaV2)}
      >
        {({ handleSubmit, values, setFieldValue, setFieldTouched, touched, errors }) => {
          return (
            <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="my-4">
                <Field
                  type="password"
                  name="password"
                  id="Password"
                  label={t('login.form.password')}
                  autoComplete="off"
                  component={ReactstrapFormikFloatingLabelPasswordInput}
                />
              </div>
              <input type="hidden" value={values.recaptchaCode} name="recaptchaCode" />
              {isRecaptchaV2 && siteKey && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  className="mb-4"
                >
                  <RecaptchaV2Net
                    ref={recaptchaRef}
                    sitekey={siteKey}
                    onChange={(value) => {
                      setFieldValue('recaptchaCode', value);
                      setFieldTouched('recaptchaCode', true);
                    }}
                    hl={
                      store?.getState()?.UserData?.language?.code ||
                      store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                    key={
                      store?.getState()?.UserData?.language?.code ||
                      store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                  />

                  <div className="text-left codeFixWidth">
                    {touched.recaptchaCode && errors.recaptchaCode !== '' && values.recaptchaCode == '' && (
                      <FormFeedback className="pb-0 error invalid-feedback d-block" type="invalid">
                        {errors.recaptchaCode}
                      </FormFeedback>
                    )}
                  </div>
                </div>
              )}
              <Form.Group className="form-group">
                <Button type="submit" className="btn btn-primary w-100 sign-up-button">
                  {t('login.button.signInWithPassword')}
                </Button>
              </Form.Group>
              <div className="form-group pb-2">
                <div className="custom-control custom-checkbox custom-control-inline">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="remember-me"
                    name="rememberMe"
                    checked={values.rememberMe}
                    onChange={() => setFieldValue('rememberMe', !values.rememberMe)}
                  />
                  <label
                    className="custom-control-label sign-up-checkbox-label pb-0"
                    htmlFor="remember-me"
                    style={{ fontSize: '14px' }}
                  >
                    {t('login.label.rememberMe')}
                  </label>
                </div>
                <a
                  href="#"
                  className="forgot-password float-right"
                  title={t<string>('login.button.forgotPassword')}
                  onClick={(e) => {
                    e.preventDefault();
                    onChangeStage(Common.AuthStage.FORGOT_PASSWORD);
                  }}
                >
                  {t('login.button.forgotPassword')}
                </a>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SignInWithPassword;
